import { BackOfficeShell } from "@adapt/ui-kit";
import { Button, Text } from "@fluentui/react-components";
import {
  CollectionsRegular,
  DataTrendingRegular,
  ShieldTaskRegular,
  SettingsRegular,
  BotRegular,
  ChevronDownRegular,
  ChevronRightRegular,
  ChevronDoubleLeftRegular,
  ChevronDoubleRightRegular,
} from "@fluentui/react-icons";
import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { listAssistantsApiV1SettingsAssistantsGet } from "@/shared/utils/api";
import type { AssistantOut } from "@/shared/utils/api";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AccordionPanel } from "@adapt/ui-kit";

const AssistantsButton = lazy(() =>
  import("@adapt/ui-kit").then((m) => ({ default: m.AssistantsButton }))
);

// ── Types ─────────────────────────────────────────────────────────────────────

interface NavLeaf {
  kind: "leaf";
  key: string;
  label: string;
  href: string;
  icon: React.ReactNode;
}

interface NavGroup {
  kind: "group";
  key: string;
  label: string;
  icon: React.ReactNode;
  children: NavLeaf[];
}

type NavItem = NavLeaf | NavGroup;

// ── Nav definition ────────────────────────────────────────────────────────────



// ── Helpers ───────────────────────────────────────────────────────────────────

function isActive(href: string, pathname: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

function groupIsActive(group: NavGroup, pathname: string): boolean {
  return group.children.some((c) => isActive(c.href, pathname));
}

// ── Shared nav-item button style ─────────────────────────────────────────────

const itemStyle = (active: boolean, collapsed: boolean, indent: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: collapsed ? 0 : "10px",
  width: "100%",
  padding: collapsed ? "10px 0" : indent ? "8px 16px 8px 40px" : "10px 16px",
  justifyContent: collapsed ? "center" : "flex-start",
  background: active ? "var(--colorNeutralBackground1Selected)" : "transparent",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  color: active ? "var(--colorBrandForeground2)" : "var(--colorNeutralForeground1)",
  fontFamily: "inherit",
  fontSize: indent ? "13px" : "14px",
  fontWeight: active ? 600 : 400,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textAlign: "left",
});

const collapseWrapStyle: React.CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: "8px 16px 8px 8px",
  borderBottom: "1px solid var(--colorNeutralStroke1)",
  flexShrink: 0,
  boxSizing: "border-box",
};

// ── Nav sub-components ───────────────────────────────────────────────────────

interface NavLeafButtonProps {
  item: NavLeaf; indent: boolean; collapsed: boolean;
  pathname: string; onNavigate: (href: string) => void;
}

function NavLeafButton({ item, indent, collapsed, pathname, onNavigate }: NavLeafButtonProps): React.JSX.Element {
  const active = isActive(item.href, pathname);
  return (
    <button key={item.key} aria-current={active ? "page" : undefined}
      onClick={() => onNavigate(item.href)} style={itemStyle(active, collapsed, indent)}>
      <span style={{ flexShrink: 0, display: "flex", fontSize: indent ? "16px" : "20px" }}>{item.icon}</span>
      {!collapsed && <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</span>}
    </button>
  );
}

interface NavGroupPanelProps {
  group: NavGroup; collapsed: boolean; pathname: string; isOpen: boolean;
  onToggle: (key: string, next: boolean) => void;
  onSetCollapsed: (v: boolean) => void; onNavigate: (href: string) => void;
}

function NavGroupPanel({ group, collapsed, pathname, isOpen, onToggle, onSetCollapsed, onNavigate }: NavGroupPanelProps): React.JSX.Element {
  const parentActive = groupIsActive(group, pathname);
  return (
    <AccordionPanel key={group.key} open={!collapsed && isOpen}
      onOpenChange={(next) => {
        if (collapsed) { onSetCollapsed(false); onToggle(group.key, true); }
        else { onToggle(group.key, next); }
      }}
      header={(open) => (
        <span style={{ ...itemStyle(parentActive, collapsed, false), display: "flex" }}>
          <span style={{ flexShrink: 0, display: "flex", fontSize: "20px" }}>{group.icon}</span>
          {!collapsed && (
            <>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group.label}</span>
              <span style={{ flexShrink: 0, display: "flex", fontSize: "14px", marginRight: "2px" }}>
                {open ? <ChevronDownRegular /> : <ChevronRightRegular />}
              </span>
            </>
          )}
        </span>
      )}
      headerStyle={{ borderRadius: "4px" }}
    >
      {group.children.map((child) => <NavLeafButton key={child.key} item={child} indent collapsed={collapsed} pathname={pathname} onNavigate={onNavigate} />)}
    </AccordionPanel>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

// ── Assistant modes hook ──────────────────────────────────────────────────

const ROUTE_CONTEXT_MAP = [
  { prefix: "/collections",         nameFragment: "Collections", pageContext: "Collections"         },
  { prefix: "/revenue-forecasting", nameFragment: "Revenue",     pageContext: "Revenue Forecasting" },
  { prefix: "/fraud",               nameFragment: "Fraud",        pageContext: "Fraud Detection"     },
] as const;

type AssistantMode = { assistantId: string; name: string; placeholder: string; pageContext?: string };

function useAssistantModes(pathname: string, placeholder: string): AssistantMode[] {
  const [modes, setModes] = useState<AssistantMode[]>([]);
  useEffect(() => {
    listAssistantsApiV1SettingsAssistantsGet()
      .then((r) => setModes(((r.data ?? []) as AssistantOut[]).filter((a) => a.isPublished).map((a) => ({ assistantId: a.id, name: a.name, placeholder }))))
      .catch(() => setModes([]));
  }, [placeholder]);
  const match = ROUTE_CONTEXT_MAP.find((m) => pathname.startsWith(m.prefix));
  const base  = match ? (modes.find((m) => m.name.includes(match.nameFragment)) ?? modes[0] ?? null) : (modes[0] ?? null);
  if (!base) return [];
  return [{ ...base, pageContext: match?.pageContext ?? "General" }];
}


function NavPanel({ nav, navCollapsed, setNavCollapsed, groupOpen, toggleGroup, pathname, navigate, t }: {
  nav: NavItem[]; navCollapsed: boolean; setNavCollapsed: (v: boolean) => void;
  groupOpen: Record<string, boolean>; toggleGroup: (key: string, next: boolean) => void;
  pathname: string; navigate: ReturnType<typeof useNavigate>;
  t: ReturnType<typeof useTranslation<"shell">>["t"];
}): React.JSX.Element {
  return (
    <div aria-label="Main navigation"
      style={{ display: "flex", flexDirection: "column", flex: 1, padding: 0, minWidth: 0 }}>
      <div style={{ ...collapseWrapStyle, justifyContent: navCollapsed ? "center" : "flex-end",
        padding: navCollapsed ? "8px 0" : "8px 16px 8px 8px" }}>
        <Button appearance="subtle" size="small"
          icon={navCollapsed ? <ChevronDoubleRightRegular /> : <ChevronDoubleLeftRegular />}
          onClick={() => setNavCollapsed(!navCollapsed)}
          aria-label={navCollapsed ? t("nav.expand") : t("nav.collapse")}
          title={navCollapsed ? t("nav.expand.title") : t("nav.collapse.title")} />
      </div>
      <div style={{ padding: "8px 4px 0" }}>
        {nav.map((item) =>
          item.kind === "group"
            ? <NavGroupPanel key={item.key} group={item} collapsed={navCollapsed} pathname={pathname}
                isOpen={!!groupOpen[item.key]} onToggle={toggleGroup} onSetCollapsed={setNavCollapsed} onNavigate={navigate} />
            : <NavLeafButton key={item.key} item={item} indent={false} collapsed={navCollapsed} pathname={pathname} onNavigate={navigate} />
        )}
      </div>
    </div>
  );
}

export function AppShell(): React.JSX.Element {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { t } = useTranslation("shell");
  const [navCollapsed, setNavCollapsed] = useState(() => window.innerWidth < 1100);
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>({});
  const toggleGroup = (key: string, next: boolean) => setGroupOpen((prev) => ({ ...prev, [key]: next }));
  const assistantModes = useAssistantModes(location.pathname, t("assistant.placeholder"));

  const NAV: NavItem[] = [
    { kind: "leaf", key: "collections",         label: t("nav.collections"),         href: "/collections",         icon: <CollectionsRegular /> },
    { kind: "leaf", key: "revenue-forecasting", label: t("nav.revenueForecasting"), href: "/revenue-forecasting", icon: <DataTrendingRegular /> },
    { kind: "leaf", key: "fraud",               label: t("nav.fraud"),               href: "/fraud",               icon: <ShieldTaskRegular /> },
    { kind: "leaf", key: "ai-assistants",       label: t("nav.aiAssistants"),        href: "/ai-assistants",       icon: <BotRegular /> },
    { kind: "leaf", key: "settings",            label: t("nav.settings"),            href: "/settings",            icon: <SettingsRegular /> },
  ];

  const customNav = <NavPanel nav={NAV} navCollapsed={navCollapsed} setNavCollapsed={setNavCollapsed} groupOpen={groupOpen} toggleGroup={toggleGroup} pathname={location.pathname} navigate={navigate} t={t} />;

  return (
    <>
      <BackOfficeShell
        appName={t("appName")}
        brand={<Text weight="semibold">{t("brand")}</Text>}
        navigationAriaLabel={t("navigationAriaLabel")}
        navigation={customNav}
        navCollapsed={navCollapsed}
        showThemeToggle
      >
        <Outlet />
      </BackOfficeShell>
      {assistantModes.length > 0 && (
        <Suspense fallback={null}>
          <AssistantsButton visible={!location.pathname.startsWith("/ai-assistants")} modes={assistantModes} />
        </Suspense>
      )}
    </>
  );
}
